import {
  cn,
  TooltipArrow,
  TooltipContent,
  type TooltipContentProps,
  TooltipProvider,
  TooltipTrigger,
  Tooltip as UITooltip,
} from '@mntn/ui-components'
import { cva } from 'class-variance-authority'
import type { ReactNode } from 'react'
import { SLATE_THEME_CLASSNAME } from './mfe-theme-wrapper'

const tooltipContentClass = cva('p-0 shadow-md shadow-black/20', {
  variants: {
    variant: {
      dark: 'text-header-inverse',
      light: 'text-header',
      warning: 'text-header',
    },
    border: {
      true: 'border-t border-primary-accent',
      false: 'border-none',
    },
    rounded: {
      true: 'rounded-lg',
      false: 'rounded-none',
    },
  },
  defaultVariants: {
    border: false,
    rounded: false,
  },
})

const tooltipBgClass = cva('', {
  variants: {
    variant: {
      dark: 'bg-dark-bg',
      light: 'bg-background',
      warning: 'bg-warning',
    },
  },
})

const tooltipArrowClass = cva('drop-shadow-md', {
  variants: {
    variant: {
      dark: 'fill-black',
      light: 'fill-white',
      warning: 'fill-warning',
    },
  },
})

type BaseTooltipProps = Omit<TooltipContentProps, 'children' | 'content'>

interface TooltipProps extends BaseTooltipProps {
  trigger: ReactNode
  triggerClassName?: string
  content: ReactNode
  contentClassName?: string
  hasArrow?: boolean
  arrowClassName?: string
  side?: TooltipContentProps['side']
  sideOffset?: TooltipContentProps['sideOffset']
  align?: TooltipContentProps['align']
  delayDuration?: number
  variant: 'dark' | 'light' | 'warning'
  border?: boolean
  rounded?: boolean
}

export const Tooltip = ({
  trigger,
  triggerClassName,
  content,
  contentClassName,
  hasArrow = true,
  arrowClassName,
  side = 'top',
  sideOffset = 8,
  align = 'center',
  delayDuration = 100,
  variant,
  border = variant === 'dark',
  rounded = variant === 'warning',
  ...props
}: TooltipProps) => {
  return (
    <TooltipProvider>
      <UITooltip delayDuration={delayDuration}>
        <TooltipTrigger asChild className={triggerClassName}>
          {trigger}
        </TooltipTrigger>
        <TooltipContent
          forceMount
          side={side}
          sideOffset={sideOffset}
          wrapperClassName={SLATE_THEME_CLASSNAME}
          className={cn(tooltipContentClass({ variant, border, rounded }), contentClassName)}
          {...props}
        >
          <div className={tooltipBgClass({ variant })}>{content}</div>
          {hasArrow && <TooltipArrow className={cn('shadow-md', tooltipArrowClass({ variant }), arrowClassName)} />}
        </TooltipContent>
      </UITooltip>
    </TooltipProvider>
  )
}
